import React from 'react';
import type { PatientGoalDetail } from '../../services/api/types.gen';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { UnclaimedClinicianName } from '../clinician/UnclaimedClinicianName';
import {
  goalCompletionDate,
  goalCompletionLabel,
} from './goalCompletionFields';

/**
 * A met or unattainable goal's completion, on the card itself (EMR-2311).
 *
 * The clinician on the next visit reads it at a glance: the status, the visit
 * date, who closed it, and an unattainable goal's reason. A completion recorded
 * in the source EMR says so, the same way imported performance rows do.
 */
const GoalCompletion: React.FC<{ goal: PatientGoalDetail }> = ({ goal }) => {
  const label = goalCompletionLabel(goal);
  if (!label) return null;
  const date = goalCompletionDate(goal);
  const name = goal.completed_by_name;
  const reason = goal.unattainable_reason;
  return (
    <div data-testid='goal-completion' className='space-y-0.5 text-xs'>
      <div className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
        <span className='font-medium text-foreground'>{label}</span>
        {date && <span>{formatDateForDisplay(date)}</span>}
        {name && (
          <span className='text-muted-foreground'>
            <UnclaimedClinicianName
              name={name}
              unclaimed={!!goal.completed_by_unclaimed_import}
            />
          </span>
        )}
        {goal.completion_recorded_externally && (
          <span className='text-muted-foreground'>External EMR</span>
        )}
      </div>
      {reason && (
        <p className='whitespace-pre-wrap text-muted-foreground [overflow-wrap:anywhere]'>
          {reason}
        </p>
      )}
    </div>
  );
};

export default GoalCompletion;
